import { useTrackedStore } from '@assets/store';
import type { Customer } from '@assets/types';
import type { FormikProps } from 'formik';
import { useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { object } from 'yup';
import { useAppDispatch } from '@/app/hooks';
import { deleteCustomerProps, updateCustomer } from '@/features/propzMedia/propzMediaSlice';
import { getNextRegStep } from '@/utils/internal functions';
import { 
  normalizeAndSplitFullName,
  createFieldBlurHandler,
  createNameValidationRules,
  createBusinessNameValidationRules
} from '@/utils/nameValidation';

const LGPD_CHECKBOX_KEY = 'lgpdCheckboxChecked';

export const useNameForm = () => {
	const history = useHistory();
	const dispatch = useAppDispatch();
	const { search, pathname } = useLocation();
	const customer = useTrackedStore().app.customer();

	const isPhysicalPerson = window.userIsFisicalPerson;
	const lgpdFlowEnabled = window.parameters.glob_LGPDFlow;

	const [rulesCheckbox, setRulesCheckbox] = useState(
		localStorage.getItem(LGPD_CHECKBOX_KEY) === 'true',
	);


	const handleFieldBlur = useCallback(
		(
			fieldName: 'firstName' | 'lastName',
			formikInstance: FormikProps<{ firstName: string; lastName: string }>,
		) => createFieldBlurHandler(fieldName, formikInstance),
		[],
	);

	const createOrUpdateCustomer = async (
		firstToSave: string,
		lastToSave: string,
	) => {
		const props: Customer = { registeredInPartnerProgram: true };

		if (isPhysicalPerson) {
			if (lastToSave) {
				props.lastName = lastToSave;
			} else if (customer.lastName) {
				await dispatch(deleteCustomerProps(['lastName']));
			}
		}

		if (firstToSave) {
			props.firstName = firstToSave;
		} else if (customer.firstName) {
			await dispatch(deleteCustomerProps(['firstName']));
		}

		return props;
	};

	const submitNameForm = async (values: {
		firstName: string;
		lastName: string;
	}) => {
		localStorage.setItem(LGPD_CHECKBOX_KEY, 'true');

		const { firstToSave, lastToSave } = normalizeAndSplitFullName(
			values.firstName,
			values.lastName,
			isPhysicalPerson,
		);
		const props = await createOrUpdateCustomer(firstToSave, lastToSave);

		await dispatch(updateCustomer(props));
	};

	const validationSchema = useMemo(() => {
		return object({
			firstName: isPhysicalPerson
				? createNameValidationRules(true, 'O nome')
				: createBusinessNameValidationRules(true, 'Razão social'),
			...(isPhysicalPerson 
				? { lastName: createNameValidationRules(false, 'O sobrenome') } 
				: {}
			),
		});
	}, [isPhysicalPerson]);

	const formik = useFormik({
		initialValues: {
			firstName: customer?.firstName || '',
			lastName: customer?.lastName || '',
		},
		validationSchema,
		onSubmit: async (value) => {
			document.body.style.cursor = 'wait';
			submitNameForm(value).finally(() => {
				document.body.style.cursor = 'default';
				formik.setSubmitting(false);
				history.push(`/cadastro-${getNextRegStep(pathname)}${search}`);
			});
		},
	});

	const isSubmitDisabled = () => {
		return (
			formik.isSubmitting ||
			!formik.isValid ||
			(lgpdFlowEnabled && !rulesCheckbox)
		);
	};

	return {
		formik,
		rulesCheckbox,
		setRulesCheckbox,
		isPhysicalPerson,
		lgpdFlowEnabled,

		handleFieldBlur,
		isSubmitDisabled,
	};
};
